import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import {
  AlertTriangle,
  Braces,
  ChevronLeft,
  ChevronRight,
  CircleDot,
  Cloud,
  Database,
  Globe2,
  HardDrive,
  Layers3,
  Network,
  Pause,
  Play,
  RadioTower,
  RotateCcw,
  Server,
  ShieldCheck,
  Smartphone,
  Zap,
} from 'lucide-react';
import type { DiagramNode, LessonDay } from '../content/types';

function NodeIcon({ kind }: { kind: DiagramNode['kind'] }) {
  const icons = {
    client: Smartphone,
    gateway: ShieldCheck,
    service: Server,
    cache: Layers3,
    queue: RadioTower,
    database: Database,
    edge: Cloud,
    network: Network,
  };
  const Icon = icons[kind];
  return <Icon aria-hidden="true" />;
}

export function SystemFlow({ diagram }: { diagram: NonNullable<LessonDay['diagram']> }) {
  const [step, setStep] = useState(0);
  const [endpointIndex, setEndpointIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const activeNode = diagram.nodes[step];
  const activeStep = diagram.steps[step];
  const activeEndpoint = diagram.endpoints[endpointIndex];
  const narration = step === 0
    ? `${activeNode.label} starts ${activeEndpoint.method} ${activeEndpoint.path}. The request now enters the latency budget.`
    : activeStep.narration;

  useEffect(() => {
    if (!playing) return;
    const timer = window.setTimeout(() => {
      if (step >= diagram.nodes.length - 1) setPlaying(false);
      else setStep((current) => current + 1);
    }, 1900);
    return () => window.clearTimeout(timer);
  }, [playing, step, diagram.nodes.length]);

  const selectEndpoint = (index: number) => {
    setEndpointIndex(index);
    setStep(0);
    setPlaying(false);
  };

  return (
    <div className="system-flow architecture-explorer">
      <div className="flow-toolbar">
        <div><span className="live-dot" /> API request simulator</div>
        <small><CircleDot /> Click any component to inspect it</small>
      </div>

      <div className="flow-endpoints" role="tablist" aria-label="Possible API endpoints">
        <div className="endpoint-heading">
          <Braces />
          <span><small>POSSIBLE ENDPOINTS</small><b>Choose a request to trace</b></span>
        </div>
        <div className="endpoint-tabs">
          {diagram.endpoints.map((item, index) => (
            <button
              type="button"
              role="tab"
              aria-selected={index === endpointIndex}
              className={index === endpointIndex ? 'active' : ''}
              key={item.id}
              onClick={() => selectEndpoint(index)}
            >
              <i className={`method method-${item.method.toLowerCase()}`}>{item.method}</i>
              <code>{item.path}</code>
            </button>
          ))}
        </div>
      </div>

      <div className="flow-stage">
        <div className="flow-stage-label"><Globe2 /> LIVE REQUEST PATH <span>{step + 1} / {diagram.nodes.length}</span></div>
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
          {diagram.edges.map(([from, to], index) => {
            const a = diagram.nodes.find((node) => node.id === from)!;
            const b = diagram.nodes.find((node) => node.id === to)!;
            const state = index < step - 1 ? 'passed' : index === step - 1 ? 'active' : '';
            return <line key={`${from}-${to}`} className={state} x1={a.x + 7} y1={a.y + 7} x2={b.x + 7} y2={b.y + 7} />;
          })}
        </svg>

        {diagram.nodes.map((node, index) => (
          <button
            type="button"
            key={node.id}
            className={`flow-node kind-${node.kind} ${index < step ? 'passed' : ''} ${index === step ? 'active' : ''} ${index > step ? 'waiting' : ''}`}
            style={{ left: `clamp(74px, ${node.x}%, calc(100% - 74px))`, top: `${node.y}%` }}
            onClick={() => { setStep(index); setPlaying(false); }}
          >
            <i><NodeIcon kind={node.kind} /></i>
            <span className="node-order">0{index + 1}</span>
            <strong>{node.label}</strong>
            <small>{node.detail}</small>
          </button>
        ))}

        <motion.div
          className="flow-packet"
          animate={{ left: `clamp(90px, ${activeNode.x + 4}%, calc(100% - 90px))`, top: `${activeNode.y + 8}%` }}
          transition={{ type: 'spring', stiffness: 75, damping: 17 }}
        >
          <span><Zap /> {activeEndpoint.method} <code>{activeEndpoint.path}</code></span>
          <i />
        </motion.div>
      </div>

      <div className="flow-inspector">
        <AnimatePresence mode="wait">
          <motion.section key={`${activeNode.id}-${activeEndpoint.id}`} initial={{ opacity: 0, y: 7 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -5 }}>
            <header>
              <span className={`component-icon kind-${activeNode.kind}`}><NodeIcon kind={activeNode.kind} /></span>
              <div><small>NOW INSPECTING · {activeNode.kind.toUpperCase()}</small><h4>{activeStep.title}</h4></div>
            </header>
            <p>{activeNode.purpose}</p>
            <div className="failure-note"><AlertTriangle /><span><small>WHAT CAN FAIL HERE</small>{activeNode.failure}</span></div>
          </motion.section>
        </AnimatePresence>

        <section className="api-contract">
          <header>
            <div><small>API CONTRACT</small><h4><i className={`method method-${activeEndpoint.method.toLowerCase()}`}>{activeEndpoint.method}</i> <code>{activeEndpoint.path}</code></h4></div>
            <HardDrive />
          </header>
          <p>{activeEndpoint.purpose}</p>
          <div className="payload-grid">
            <div><small>REQUEST</small><pre>{activeEndpoint.request}</pre></div>
            <div><small>RESPONSE</small><pre>{activeEndpoint.response}</pre></div>
          </div>
        </section>
      </div>

      <div className="flow-narration">
        <span>0{step + 1}</span>
        <div className="narration-copy"><b>{activeEndpoint.method} {activeEndpoint.path}</b><p>{narration}</p></div>
        <div className="flow-controls">
          <button type="button" aria-label="Restart" onClick={() => { setStep(0); setPlaying(false); }}><RotateCcw /></button>
          <button type="button" aria-label="Previous component" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}><ChevronLeft /></button>
          <button type="button" aria-label={playing ? 'Pause flow' : 'Play flow'} className="play-round" onClick={() => { if (step === diagram.nodes.length - 1) setStep(0); setPlaying(!playing); }}>{playing ? <Pause /> : <Play fill="currentColor" />}</button>
          <button type="button" aria-label="Next component" onClick={() => setStep(Math.min(diagram.nodes.length - 1, step + 1))} disabled={step === diagram.nodes.length - 1}><ChevronRight /></button>
        </div>
      </div>
    </div>
  );
}
